import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { getAuthToken } from "../utils/authToken";

const CartContext = createContext();

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cartLoading, setCartLoading] = useState(false);

  // Debounce timers for quantity updates: { [productId]: timeoutId }
  const qtyTimers = useRef({});

  const openCart  = () => setDrawerOpen(true);
  const closeCart = () => setDrawerOpen(false);

  useEffect(() => {
    if (user === undefined) return;
    if (user) fetchCart();
    else setLoading(false);
  }, [user]);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("No token");
      const res = await fetch("/api/cart", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        console.error("fetchCart failed:", res.status, await res.text());
        return;
      }
      const data = await res.json();
      setCart(data.map(item => ({
        id:         item.id,
        product_id: item.product_id,
        quantity:   item.quantity,
        product: {
          id:        item.product_id,
          title:     item.title,
          author:    item.author,
          price:     item.price,
          cover_url: item.cover_url,
          stock:     item.stock,
        },
      })));
    } catch (err) {
      console.error("Failed to fetch cart:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Add to cart — optimistic ──────────────────────────────────────────────
  const addToCart = async (product, { open = true } = {}) => {
    // 1. Update UI instantly
    const snapshot = cart;
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        return prev.map(i =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, {
        product_id: product.id,
        product,
        quantity: 1,
        id: `optimistic_${Date.now()}`,
      }];
    });
    if (open) setDrawerOpen(true);

    if (!user) return; // guest — local only

    // 2. Sync in background
    setCartLoading(true);
    try {
      const token = await getAuthToken();
      if (!token) { setCart(snapshot); return; }

      const res = await fetch("/api/cart", {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ product_id: product.id, quantity: 1 }),
      });

      if (!res.ok) {
        console.error("Add to cart failed:", res.status, await res.text());
        setCart(snapshot); // rollback
        return;
      }
      // Reconcile to get the real DB id for the new item
      await fetchCart();
    } catch (err) {
      console.error("Failed to add to cart:", err);
      setCart(snapshot);
    } finally {
      setCartLoading(false);
    }
  };

  // ── Remove from cart — optimistic ─────────────────────────────────────────
  const removeFromCart = async (productId) => {
    const snapshot = cart;
    setCart(prev => prev.filter(i => i.product_id !== productId));

    if (!user) return;

    const cartItem = snapshot.find(i => i.product_id === productId);
    if (!cartItem) return;

    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/cart/${cartItem.id}`, {
        method:  "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) setCart(snapshot); // rollback
    } catch (err) {
      console.error("Failed to remove from cart:", err);
      setCart(snapshot);
    }
  };

  // ── Update quantity — optimistic + debounced API call ─────────────────────
  const updateQuantity = async (productId, quantity) => {
    if (quantity < 1) return;

    // Optimistic UI update immediately
    const snapshot = cart;
    setCart(prev =>
      prev.map(i => i.product_id === productId ? { ...i, quantity } : i)
    );

    if (!user) return;

    // Debounce the API call — if user clicks +/- rapidly, only fire once
    clearTimeout(qtyTimers.current[productId]);
    qtyTimers.current[productId] = setTimeout(async () => {
      const cartItem = snapshot.find(i => i.product_id === productId);
      if (!cartItem) return;
      try {
        const token = await getAuthToken();
        const res = await fetch(`/api/cart/${cartItem.id}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ quantity }),
        });
        if (!res.ok) {
          console.error("Update quantity failed:", res.status);
          setCart(snapshot); // rollback
        }
      } catch (err) {
        console.error("Failed to update cart quantity:", err);
        setCart(snapshot);
      }
    }, 400);
  };

  // ── Clear cart ─────────────────────────────────────────────────────────────
  const clearCart = async () => {
    setCart([]);
    if (!user) return;
    try {
      const token = await getAuthToken();
      await fetch("/api/cart/clear", {
        method:  "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error("Failed to clear cart:", err);
    }
  };

  const total     = cart.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  const itemCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart, addToCart, removeFromCart, updateQuantity, clearCart,
      total, itemCount,
      drawerOpen, openCart, closeCart,
      loading, cartLoading,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
